import { z } from "zod";
import { NumParam, Slug, Timestamp } from "./common.ts";
import type { Lv95 } from "./lv95.ts";
import type { Terrain } from "./terrain-codec.ts";

/**
 * Dive mode (beta): a first-person swim through a whole lake.
 *
 * Access: admins always; any other signed-in, non-banned user only when an admin granted it
 * (`AdminUser.diveAccess`, written ONLY by PATCH /api/admin/users/:id — if the column is ever
 * exposed to better-auth it must be `input: false`, so /api/auth/update-user cannot set it).
 * The client learns its access from GET /api/me/features. Every /api/dive/* route has auth
 * "dive" (see routes.ts). This is a feature gate, not data protection: the same swisstopo data
 * is public in small pieces via /api/terrain.
 *
 * Data: a per-lake tile set pre-computed once by the API's `dive:tiles` job (never in the
 * request path) and served as files. Tiles are TERRAIN binaries (encodeTerrain/decodeTerrain),
 * so NaN nodata, the source byte and LV95 origins mean exactly what they mean for /api/terrain.
 *
 * Grid: square tiles of DIVE_TILE_CELLS cells. Each tile holds DIVE_TILE_SIDE = cells + 1
 * samples per side, so neighbours share their edge row/column and stitch without seams
 * (129² vertices fit a Uint16 index). Tile (x, y): x grows east, y grows south, (0, 0) is the
 * north-west tile. Its sample (0, 0) — its north-west sample — sits at
 * `diveTileOriginLv95(manifest, x, y)`. MVP: cellSizeM = max(DIVE_CELL_SIZE_M, lake bathy
 * resolution), and samples are aligned to the bathy source's pixel centres where the spacing is a
 * multiple of the resolution, so dive depths match /api/depth within the resolution.
 * Only tiles with at least one bathy cell are built; land cells inside them are kept.
 *
 * Tile and overview metas: `waterLevelM: null`, `lakeSlug` = the lake, `center` = the WGS84
 * of the middle sample, `radiusM` = round(half the sample span). The ONLY water level is
 * `DiveManifest.waterLevelM`, which the API fills from the lakes table at request time (an
 * admin correction applies without a rebuild). Clients MUST pass every decoded tile/overview
 * through `withDiveWaterLevel` before any terrain maths: depth = waterLevelM − elevation.
 * The ONE local origin of a dive scene is `manifest.originLv95` — never a tile's `meta.center`.
 */
export const DIVE_TILE_CELLS = 128;
export const DIVE_TILE_SIDE = DIVE_TILE_CELLS + 1;
/** MVP sample spacing in metres (swim visibility is a few metres; finer adds only bytes). */
export const DIVE_CELL_SIZE_M = 2;
/** The overview is one terrain binary over the whole grid, at most this many samples per side. */
export const DIVE_OVERVIEW_MAX_SIDE = 512;

/**
 * Build id of a tile set (e.g. "20261021T1530Z"). Only ever compared — never joined into a
 * file path. Must start with a letter or digit, so "." and ".." are invalid.
 */
export const DiveVersion = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/);

export const DiveManifest = z
  .object({
    lakeSlug: Slug,
    version: DiveVersion,
    builtAt: Timestamp,
    /** Current lake level (LN02), from the lakes table — not the build snapshot. */
    waterLevelM: z.number(),
    /** Sample spacing in metres, the same for every tile (MVP: max(DIVE_CELL_SIZE_M, bathy res)). */
    cellSizeM: z.number().min(0.5).max(64),
    tileCells: z.literal(DIVE_TILE_CELLS),
    /** LV95 of sample (0, 0) of tile (0, 0) — also the local origin of the dive scene. */
    originLv95: z.object({ e: z.number(), n: z.number() }).strict(),
    cols: z.number().int().min(1).max(4096),
    rows: z.number().int().min(1).max(4096),
    /**
     * The tiles that exist, as [x, y], sorted by y then x, no duplicates. A tile that is not
     * listed is "no data" (GET → 404), never a flat 0 m bed.
     */
    tiles: z.array(z.tuple([z.number().int().min(0), z.number().int().min(0)])).min(1),
    /** Over all data cells of all tiles. */
    minElevM: z.number(),
    maxElevM: z.number(),
    attribution: z.string(),
  })
  .strict()
  .refine((m) => m.minElevM <= m.maxElevM, { message: "minElevM > maxElevM", path: ["minElevM"] })
  .refine((m) => m.minElevM < m.waterLevelM, {
    message: "the lakebed must lie below the water level",
    path: ["waterLevelM"],
  })
  .refine((m) => m.tiles.every(([x, y]) => x < m.cols && y < m.rows), {
    message: "tile outside cols/rows",
    path: ["tiles"],
  })
  .refine(
    (m) =>
      m.tiles.every(([x, y], i) => {
        if (i === 0) return true;
        const [px, py] = m.tiles[i - 1]!;
        return y > py || (y === py && x > px);
      }),
    { message: "tiles must be sorted by y then x without duplicates", path: ["tiles"] },
  );
export type DiveManifest = z.infer<typeof DiveManifest>;

/** One row of GET /api/dive/lakes: a lake whose tile set has been built. */
export const DiveLake = z
  .object({
    slug: Slug,
    version: DiveVersion,
    builtAt: Timestamp,
    tileCount: z.number().int().min(1),
  })
  .strict();
export type DiveLake = z.infer<typeof DiveLake>;

export const DiveLakeList = z.object({ lakes: z.array(DiveLake) }).strict();
export type DiveLakeList = z.infer<typeof DiveLakeList>;

/** `:slug/:x/:y` of GET /api/dive/:slug/tiles/:x/:y. */
export const DiveTileParams = z
  .object({
    slug: Slug,
    x: NumParam.pipe(z.number().int().min(0).max(4095)),
    y: NumParam.pipe(z.number().int().min(0).max(4095)),
  })
  .strict();
export type DiveTileParams = z.infer<typeof DiveTileParams>;

/**
 * `?v=` on tile and overview requests.
 * - Equal to the current manifest version → `Cache-Control: private, max-age=31536000, immutable`.
 * - Absent → the current body with `Cache-Control: private, no-cache`.
 * - Different → 409 conflict "dive set rebuilt": the client refetches the manifest and starts
 *   over. (A tile from another build may have another grid; it must never be placed silently.)
 */
export const DiveTileQuery = z.object({ v: DiveVersion.optional() }).strict();
export type DiveTileQuery = z.infer<typeof DiveTileQuery>;

/** LV95 of sample (0, 0) — the north-west sample — of tile (x, y). */
export function diveTileOriginLv95(
  m: Pick<DiveManifest, "originLv95" | "cellSizeM">,
  x: number,
  y: number,
): Lv95 {
  const span = DIVE_TILE_CELLS * m.cellSizeM;
  return { e: m.originLv95.e + x * span, n: m.originLv95.n - y * span };
}

/**
 * The tile whose sample span [origin, origin + DIVE_TILE_CELLS·cellSizeM) contains `p` (east and
 * south), or null outside that. For STREAMING only: a point on a shared edge belongs to the tile
 * east/south of it, and the outermost east/south samples of the grid return null although they
 * hold data — so collision and depth must never read null here as "no data". Says nothing about
 * whether the tile exists (see `manifest.tiles`).
 */
export function diveTileAt(
  m: Pick<DiveManifest, "originLv95" | "cellSizeM" | "cols" | "rows">,
  p: Lv95,
): { x: number; y: number } | null {
  const span = DIVE_TILE_CELLS * m.cellSizeM;
  const x = Math.floor((p.e - m.originLv95.e) / span);
  const y = Math.floor((m.originLv95.n - p.n) / span);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (x < 0 || y < 0 || x >= m.cols || y >= m.rows) return null;
  return { x, y };
}

/** Stable key for maps/sets of tiles. */
export const diveTileKey = (x: number, y: number): string => `${x}/${y}`;

/**
 * A decoded dive tile or overview with the manifest's live water level in its meta, so the
 * existing terrain maths (depth, lake/land classification) works unchanged. Pure: returns a
 * new object sharing the sample arrays. Throws if the terrain is not of the manifest's lake.
 */
export function withDiveWaterLevel(
  t: Terrain,
  m: Pick<DiveManifest, "lakeSlug" | "waterLevelM">,
): Terrain {
  if (t.meta.lakeSlug !== m.lakeSlug) {
    throw new Error(`dive: tile of ${t.meta.lakeSlug ?? "no lake"} under manifest ${m.lakeSlug}`);
  }
  return { ...t, meta: { ...t.meta, waterLevelM: m.waterLevelM } };
}

/** GET /api/me/features — effective feature access of the caller, checked on every request. */
export const MeFeatures = z.object({ dive: z.boolean() }).strict();
export type MeFeatures = z.infer<typeof MeFeatures>;
